import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import styles from "./../DemoPage.module.css";
import c from "./ClinicalCase.module.css";
import PathwayGraph from "./PathwayGraph";

const CONF = {
  high: c.confHigh,
  medium: c.confMedium,
  low: c.confLow,
};

const TIER = {
  ESTABLISHED: c.tierEstablished,
  INFERRED: c.tierInferred,
  HYPOTHESIS: c.tierHypothesis,
};

export default function ClinicalCase({
  tag = "MitoAgent · Live Demo",
  title,
  highlight,
  subtitle,
  stats = [],
  drugSafety,
  presentation = [],
  clinicianQuote,
  assessment,
  pathway,
  hypotheses = [],
  mechanism = [],
  investigations = [],
  adversarial = [],
  conclusion,
}) {
  // Build the section list dynamically so nav matches what's actually rendered
  const sections = [
    { id: "case", label: "The case", show: presentation.length > 0 },
    { id: "clinician", label: "Referring clinician", show: !!clinicianQuote },
    { id: "assessment", label: "MitoAgent assessment", show: !!assessment },
    { id: "pathway", label: "Reasoning pathway", show: !!pathway },
    { id: "mechanism", label: "Mechanism", show: mechanism.length > 0 },
    {
      id: "investigations",
      label: "Investigations",
      show: investigations.length > 0,
    },
    {
      id: "adversarial",
      label: "Adversarial testing",
      show: adversarial.length > 0,
    },
    { id: "conclusion", label: "Conclusion", show: !!conclusion },
  ].filter((s) => s.show);

  const [active, setActive] = useState(sections[0]?.id);
  const observerRef = useRef(null);

  useEffect(() => {
    const opts = { rootMargin: "-30% 0px -60% 0px", threshold: 0 };
    const cb = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) setActive(entry.target.id);
      });
    };
    observerRef.current = new IntersectionObserver(cb, opts);
    sections.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) observerRef.current.observe(el);
    });
    return () => observerRef.current?.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) {
      const y = el.getBoundingClientRect().top + window.scrollY - 100;
      window.scrollTo({ top: y, behavior: "smooth" });
    }
  };

  return (
    <div className={styles.page}>
      <div className={styles.background}>
        <div className={styles.glow} />
      </div>

      <div className={c.shell}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Link to="/" className={styles.backLink}>
            &larr; Back to Home
          </Link>

          {/* Header */}
          <div className={c.header}>
            <span className={c.tag}>{tag}</span>
            <h1 className={c.title}>
              {title}{" "}
              {highlight && <span className={c.highlight}>{highlight}</span>}
            </h1>
            {subtitle && <p className={c.subtitle}>{subtitle}</p>}
            {stats.length > 0 && (
              <div className={c.statsRow}>
                {stats.map((s) => (
                  <div key={s.label} className={c.statItem}>
                    <span className={c.statLabel}>{s.label}</span>
                    <span className={c.statValue}>{s.value}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </motion.div>

        {/* Two-column: sticky nav + flowing content */}
        <div className={c.layout}>
          {/* Sticky nav */}
          <nav className={c.nav}>
            <div className={c.navInner}>
              {sections.map((s) => (
                <button
                  key={s.id}
                  onClick={() => scrollTo(s.id)}
                  className={`${c.navItem} ${active === s.id ? c.navActive : ""}`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </nav>

          {/* Content */}
          <div className={c.content}>
            {/* Drug safety callout (optional, sits above the case) */}
            {drugSafety && (
              <div className={c.alert}>
                <p className={c.alertTitle}>Drug safety finding</p>
                <p className={c.alertBody}>{drugSafety}</p>
              </div>
            )}

            {/* THE CASE */}
            {presentation.length > 0 && (
              <section id="case" className={c.section}>
                <h2 className={c.sectionTitle}>The case</h2>
                <table className={c.dataTable}>
                  <tbody>
                    {presentation.map((row) => (
                      <tr key={row.label}>
                        <td className={c.dataLabel}>{row.label}</td>
                        <td className={c.dataValue}>
                          {row.badges ? (
                            <div className={c.badges}>
                              {row.badges.map((b) => (
                                <span key={b} className={c.badge}>
                                  {b}
                                </span>
                              ))}
                            </div>
                          ) : (
                            row.value
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </section>
            )}

            {/* CLINICIAN */}
            {clinicianQuote && (
              <section id="clinician" className={c.section}>
                <h2 className={c.sectionTitle}>Referring clinician</h2>
                <blockquote className={c.quote}>{clinicianQuote}</blockquote>
              </section>
            )}

            {/* ASSESSMENT */}
            {assessment && (
              <section id="assessment" className={c.section}>
                <h2 className={c.sectionTitle}>MitoAgent assessment</h2>
                <div className={c.assessment}>
                  <p className={c.assessmentLabel}>{assessment.verdict}</p>
                  <p className={c.assessmentText}>{assessment.text}</p>
                </div>
              </section>
            )}

            {/* PATHWAY (MELAS only) */}
            {pathway && (
              <section id="pathway" className={c.section}>
                <h2 className={c.sectionTitle}>Reasoning pathway</h2>
                <PathwayGraph graph={pathway} />
              </section>
            )}

            {/* HYPOTHESES */}
            {hypotheses.length > 0 && (
              <section id="hypotheses" className={c.section}>
                <h2 className={c.sectionTitle}>Hypotheses</h2>
                {hypotheses.map((h, i) => (
                  <div key={i} className={c.hypothesis}>
                    <div className={c.hypothesisHead}>
                      <span className={c.hNum}>H{i + 1}</span>
                      <span className={c.hTitle}>{h.title}</span>
                      <span
                        className={`${c.conf} ${CONF[h.confidence] || c.confLow}`}
                      >
                        {h.confidenceLabel || h.confidence}
                      </span>
                    </div>
                    <p className={c.hText}>{h.text}</p>
                  </div>
                ))}
              </section>
            )}

            {/* MECHANISM */}
            {mechanism.length > 0 && (
              <section id="mechanism" className={c.section}>
                <h2 className={c.sectionTitle}>Mechanism</h2>
                {mechanism.map((m, i) => (
                  <div key={i} className={c.mechItem}>
                    <span
                      className={`${c.tier} ${TIER[m.tier] || c.tierInferred}`}
                    >
                      {m.tier}
                    </span>
                    <p className={c.mechText}>{m.text}</p>
                    {m.source && (
                      <p className={c.mechSource}>Source: {m.source}</p>
                    )}
                    {m.prediction && (
                      <div className={c.prediction}>
                        <p>
                          <strong>Testable prediction:</strong> {m.prediction}
                        </p>
                        {m.test && (
                          <p>
                            <strong>Proposed test:</strong> {m.test}
                          </p>
                        )}
                        {m.decision && (
                          <p>
                            <strong>Decision rule:</strong> {m.decision}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </section>
            )}

            {/* INVESTIGATIONS */}
            {investigations.length > 0 && (
              <section id="investigations" className={c.section}>
                <h2 className={c.sectionTitle}>Investigations</h2>
                {investigations.map((group, gi) => (
                  <div key={gi} className={c.invGroup}>
                    {group.heading && (
                      <p className={c.invHeading}>{group.heading}</p>
                    )}
                    {group.items.map((inv, ii) => (
                      <div key={ii} className={c.invCard}>
                        <p className={c.invTitle}>{inv.title}</p>
                        {inv.why && (
                          <p className={c.invWhy}>
                            <strong>Why:</strong> {inv.why}
                          </p>
                        )}
                        {inv.decision && (
                          <p className={c.invDecision}>
                            <strong>Decision rule:</strong> {inv.decision}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                ))}
              </section>
            )}

            {/* ADVERSARIAL */}
            {adversarial.length > 0 && (
              <section id="adversarial" className={c.section}>
                <h2 className={c.sectionTitle}>Adversarial testing</h2>
                <p className={c.adversarialIntro}>
                  MitoAgent actively tries to refute each hypothesis before
                  generating the brief.
                </p>
                {adversarial.map((a, i) => (
                  <div key={i} className={c.advCard}>
                    <div className={c.advHead}>
                      <span className={c.advHyp}>{a.hypothesis}</span>
                      <span
                        className={`${c.advResult} ${a.refuted ? c.resultRefuted : c.resultSurvives}`}
                      >
                        {a.result}
                      </span>
                    </div>
                    {a.against && (
                      <p className={c.advLine}>
                        <strong>Strongest argument against:</strong> {a.against}
                      </p>
                    )}
                    {a.survives && (
                      <p className={c.advLine}>
                        <strong>Why it survives:</strong> {a.survives}
                      </p>
                    )}
                    {a.vulnerability && (
                      <p className={c.advLine}>
                        <strong>Key vulnerability:</strong> {a.vulnerability}
                      </p>
                    )}
                    {a.refutation && (
                      <p className={c.advLine}>
                        <strong>Best refutation test:</strong> {a.refutation}
                      </p>
                    )}
                    {a.refutedReason && (
                      <p className={c.advLine}>
                        <strong>Refuted:</strong> {a.refutedReason}
                      </p>
                    )}
                  </div>
                ))}
              </section>
            )}

            {/* CONCLUSION */}
            {conclusion && (
              <section id="conclusion" className={c.section}>
                <h2 className={c.sectionTitle}>Conclusion</h2>
                <div className={c.conclusion}>
                  {conclusion.title && (
                    <p className={c.conclusionTitle}>{conclusion.title}</p>
                  )}
                  <p className={c.conclusionText}>{conclusion.text}</p>
                </div>
              </section>
            )}

            {/* Disclaimer */}
            <div className={c.disclaimer}>
              Research tool only. MitoAgent is not a diagnostic system and does
              not constitute medical advice. All clinical decisions must be made
              by qualified healthcare professionals. Outputs should be
              independently verified by domain experts. This demo uses a
              synthetic case for illustrative purposes.
            </div>

            <div className={styles.cta}>
              <p className={styles.ctaText}>
                Run your own case through MitoAgent.
              </p>
              <Link to="/playground" className={styles.ctaButton}>
                Apply for Early Access &rarr;
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
